import { useCallback, useMemo } from 'react';

import { useProviderConfigController_getLocationConfig } from '@/services/hooks';

import { type ProviderConfigError, parsedErrorOrNull } from './errors';
import { mapLocationConfig } from './mappers';
import type { LocationProviderConfig } from './types';

export interface UseLocationProviderConfigResult {
    data: LocationProviderConfig | null;
    isLoading: boolean;
    isFetching: boolean;
    error: ProviderConfigError | null;
    /** Resolves with the fresh config, so a conflict merge can use it right away. */
    refetch: () => Promise<LocationProviderConfig | null>;
}

export function isValidLocationId(locationId: number): boolean {
    return Number.isInteger(locationId) && locationId > 0;
}

/**
 * A site's provider config. It refetches on focus and on every mount, unlike the
 * project's defaults: an admin who changed a row in another tab must not save over it
 * from a stale page without the optimistic check telling them. It is stale at once, since
 * the project's five-minute staleTime would otherwise skip the refetch on a focus soon
 * after the last fetch.
 */
export function useLocationProviderConfig(locationId: number): UseLocationProviderConfigResult {
    const query = useProviderConfigController_getLocationConfig(locationId, {
        enabled: isValidLocationId(locationId),
        staleTime: 0,
        refetchOnWindowFocus: true,
        refetchOnMount: 'always',
    });
    const dto = query.data?.data;
    const data = useMemo(() => (dto ? mapLocationConfig(dto) : null), [dto]);
    const error = useMemo(() => parsedErrorOrNull(query.error), [query.error]);
    const { refetch: refetchQuery } = query;

    const refetch = useCallback(async () => {
        const result = await refetchQuery();
        const fresh = result.data?.data;
        return fresh ? mapLocationConfig(fresh) : null;
    }, [refetchQuery]);

    return {
        data,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        error,
        refetch,
    };
}
